import React, { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { TimelineItem, TimelineSeparator, TimelineConnector, TimelineOppositeContent } from '@mui/lab';
import { TaskMilestone } from '../../../redux/slices/slice_types';
import DateTimeDisplay from '../../_common/DateTimeDisplay';
import JobMilestoneDot from './JobMilestoneDot';
import JobMilestoneStatus from './JobMilestoneStatus';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectJob } from '@/redux/selectors/jobSelectors';

interface JobMilestoneProps {
  milestone: TaskMilestone;
  isFirstPending: boolean;
  isLast: boolean;
  prevMilestone: TaskMilestone;
  jobCreatedAt?: string;
}

const errorStatuses = ['failed', 'in_error', 'complete_with_errors'];

const JobMilestone = ({ milestone, isFirstPending, isLast, prevMilestone, jobCreatedAt }: JobMilestoneProps) => {
  const enableMultiTaskLink = useContext(ConfigContext)?.enableMultiTaskLinkInTimeline || false;
  const { embargo_date, embargo_resumed_at } = useAppSelector(selectJob);
  const { completed_at, updated_at, task } = milestone;

  const isComplete = !!completed_at;
  const isFinalCompleted = isLast && !!completed_at;
  const isFailed = task.status ? errorStatuses.includes(task.status) : false;
  const isOnHold =
    !!embargo_date &&
    !embargo_resumed_at &&
    isFirstPending &&
    milestone.tasks?.find((task) => task.is_holdable_task) &&
    (task.status === 'ready' || task.status === 'in_progress');

  let status = 'failed';
  if (task.status === 'skipped') status = 'skipped';
  else if (task.status === 'rejected') status = 'rejected';
  else if (task.status === 'accepted') status = 'accepted';
  else if (isFailed) status = 'failed';
  else if (isComplete) status = 'complete';
  else if (isOnHold) status = 'hold';
  else if (isFirstPending) status = 'active';
  else status = 'future';

  const showTicker = status !== 'skipped' && isFirstPending && !isFailed && !isOnHold && task?.status !== 'on_hold';

  const getRightView = () => {
    if ((isComplete || task.status === 'accepted') && completed_at) {
      return <DateTimeDisplay>{completed_at}</DateTimeDisplay>;
    } else if (isFailed && updated_at) {
      return <DateTimeDisplay>{updated_at}</DateTimeDisplay>;
    } else if (isOnHold) {
      return (
        <div>
          until <DateTimeDisplay>{embargo_date}</DateTimeDisplay>
        </div>
      );
    } else if (status === 'active' && (task.status === 'cancelled' || task.status === 'cancelling')) {
      return (
        <CancelledAt>
          <CancelledLabel>{task.status.charAt(0).toUpperCase() + task.status.slice(1)}</CancelledLabel>
        </CancelledAt>
      );
    }
  };

  const prettyName = useMemo(() => {
    if (milestone.status === 'on_hold') return 'On Hold';
    return milestone.name_pretty;
  }, [milestone]);

  return (
    <MilestoneTimelineItem active={+isFirstPending} finalcompleted={+isFinalCompleted}>
      {/* the right side of the timeline - for dates */}
      <TimelineOppositeContent>{getRightView()}</TimelineOppositeContent>

      <TimelineSeparator sx={{ minWidth: 28, maxWidth: 28 }}>
        <JobMilestoneDot
          status={isFailed && !isFirstPending ? 'complete' : status}
          cancelled={status === 'active' && task && (task.status === 'cancelled' || task.status === 'cancelling')}
          isFinalCompleted={isFinalCompleted}
          isAssigned={!!task.assigned_user}
        />
        {!isLast && <TimelineConnector sx={{ opacity: isComplete ? 0.8 : 0.3 }} />}
      </TimelineSeparator>

      {/* the left side of the timeline - for the milestone name, ticker, and task link */}
      <JobMilestoneStatus
        status={status}
        prettyName={prettyName}
        showTicker={showTicker}
        isOnHold={isOnHold || false}
        tickerStartFrom={prevMilestone?.completed_at || jobCreatedAt}
        isFinalCompleted={isFinalCompleted}
        isFirstPending={isFirstPending}
        task={task}
        multipleTasks={enableMultiTaskLink ? milestone.tasks?.filter((t) => !!t.is_manual_task) : undefined}
      />
    </MilestoneTimelineItem>
  );
};

export default JobMilestone;

const MilestoneTimelineItem = styled(TimelineItem)<{ active: number; finalcompleted: number }>`
  min-height: ${({ active }) => (active ? '120px' : '100px')}};
  
  .MuiTimelineContent-root.MuiTypography-root {
    padding-top: ${({ theme }) => theme.spacing(1)};
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    font-size: 1.1rem;
  }

  .MuiTimelineOppositeContent-root {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    font-size: 1rem;
    opacity: 0.8;
    padding-right:0;

    ${({ theme }) => theme.breakpoints.up('md')} {
      max-width: 120px;
    }

    ${({ theme }) => theme.breakpoints.up('xl')} {
      min-width: 180px;
    }
  }
`;

const CancelledLabel = styled.div`
  padding: ${({ theme }) => `${theme.spacing(0.75)} 0`};
  line-height: 1;
  opacity: 0.6;
`;

const CancelledAt = styled.div`
  opacity: 0.8;
`;
